/*
 * look-builder.js
 * Powers the "Complete the Look" product block.
 */

if (!customElements.get('look-builder')) {
  customElements.define(
    'look-builder',
    class LookBuilder extends HTMLElement {
      connectedCallback() {
        this.cart =
          document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        this.moneyFormat = this.dataset.moneyFormat || '${{amount}}';
        this.cards = Array.from(this.querySelectorAll('[data-look-card]'));
        this.submitButton = this.querySelector('[data-look-submit]');
        this.totalEl = this.querySelector('[data-look-total]');
        this.compareEl = this.querySelector('[data-look-compare]');
        this.errorWrapper = this.querySelector('.product-form__error-message-wrapper');
        this.errorMessage = this.querySelector('.product-form__error-message');
        this.selectAllEl = this.querySelector('[data-look-select-all]');

        this.cards.forEach((card) => this.initCard(card));

        if (this.selectAllEl) {
          this.selectAllEl.addEventListener('change', this.onSelectAll.bind(this));
        }

        if (this.submitButton) {
          this.submitButton.addEventListener('click', this.onSubmit.bind(this));
        }

        this.updateTotals();
      }

      initCard(card) {
        const dataEl = card.querySelector('[data-look-variants]');
        let variants = [];

        try {
          variants = JSON.parse(dataEl.textContent);
        } catch (error) {
          console.error('[look-builder] Could not parse variant data', error);
          card.dataset.lookResolved = 'false';
          return;
        }

        card.lookVariants = variants;

        card.querySelectorAll('[data-look-option]').forEach((input) => {
          input.addEventListener('change', () => {
            this.resolveCard(card);
            this.updateTotals();
          });
        });

        const checkbox = card.querySelector('[data-look-toggle]');

        if (checkbox) {
          checkbox.addEventListener('change', () => {
            this.syncCardState(card);
            this.updateTotals();
          });
        }

        this.resolveCard(card);
      }

      selectedOptions(card) {
        return Array.from(card.querySelectorAll('[data-look-option]:checked')).map(
          (input) => input.value
        );
      }

      findVariant(card) {
        const variants = card.lookVariants || [];

        if (card.dataset.lookHasOptions !== 'true') {
          return variants[0] || null;
        }

        const selected = this.selectedOptions(card);

        return (
          variants.find(
            (variant) =>
              variant.o.length === selected.length &&
              variant.o.every((value, index) => value === selected[index])
          ) || null
        );
      }

      updateOptionAvailability(card) {
        const variants = card.lookVariants || [];
        const groups = card.querySelectorAll('[data-look-option-group]');

        groups.forEach((group) => {
          const position = parseInt(group.dataset.lookOptionGroup, 10) - 1;
          const selected = this.selectedOptions(card);

          group.querySelectorAll('[data-look-option]').forEach((input) => {
            const candidate = selected.slice();
            candidate[position] = input.value;

            const match = variants.find((variant) =>
              variant.o.every((value, index) =>
                index === position ? value === input.value : value === candidate[index]
              )
            );

            input.classList.toggle('disabled', !(match && match.a));
          });
        });
      }

      resolveCard(card) {
        this.updateOptionAvailability(card);

        const variant = this.findVariant(card);
        const checkbox = card.querySelector('[data-look-toggle]');
        const priceEl = card.querySelector('[data-look-price]');
        const statusEl = card.querySelector('[data-look-status]');
        const mediaEl = card.querySelector('[data-look-media] img');

        card.lookVariant = variant;

        const purchasable = Boolean(variant && variant.a);
        card.dataset.lookResolved = purchasable ? 'true' : 'false';

        if (checkbox) {
          checkbox.disabled = !purchasable;

          if (!purchasable) {
            checkbox.checked = false;
          }
        }

        if (priceEl && variant) {
          priceEl.innerHTML = this.renderPrice(variant);
        }

        if (statusEl) {
          if (!variant) {
            statusEl.textContent = statusEl.dataset.unavailableText;
            statusEl.hidden = false;
          } else if (!variant.a) {
            statusEl.textContent = statusEl.dataset.soldOutText;
            statusEl.hidden = false;
          } else if (variant.pre) {
            statusEl.textContent = statusEl.dataset.preorderText;
            statusEl.hidden = false;
          } else {
            statusEl.hidden = true;
          }
        }

        if (mediaEl && variant) {
          if (mediaEl.dataset.lookDefaultSrc === undefined) {
            mediaEl.dataset.lookDefaultSrc = mediaEl.getAttribute('src') || '';
            mediaEl.dataset.lookDefaultSrcset = mediaEl.getAttribute('srcset') || '';
          }

          if (variant.img) {
            mediaEl.src = variant.img;
            mediaEl.srcset = '';
          } else {
            mediaEl.src = mediaEl.dataset.lookDefaultSrc;
            mediaEl.srcset = mediaEl.dataset.lookDefaultSrcset;
          }
        }

        this.syncCardState(card);
      }

      syncCardState(card) {
        const checkbox = card.querySelector('[data-look-toggle]');
        const selected = Boolean(checkbox && checkbox.checked && !checkbox.disabled);

        card.classList.toggle('look-card--selected', selected);
        card.classList.toggle('look-card--disabled', card.dataset.lookResolved !== 'true');
      }

      selectedCards() {
        return this.cards.filter((card) => {
          const checkbox = card.querySelector('[data-look-toggle]');

          return checkbox && checkbox.checked && !checkbox.disabled && card.lookVariant;
        });
      }

      updateTotals() {
        const selected = this.selectedCards();

        const total = selected.reduce((sum, card) => sum + card.lookVariant.p, 0);

        const compare = selected.reduce(
          (sum, card) => sum + (card.lookVariant.cap || card.lookVariant.p),
          0
        );

        if (this.totalEl) {
          this.totalEl.innerHTML = this.formatMoney(total);
        }

        if (this.compareEl) {
          const onSale = compare > total;
          this.compareEl.hidden = !onSale;

          if (onSale) {
            this.compareEl.innerHTML = this.formatMoney(compare);
          }
        }

        if (this.submitButton) {
          this.submitButton.disabled = selected.length === 0;

          const label = this.submitButton.querySelector('[data-look-submit-text]');

          if (label) {
            label.textContent =
              selected.length === 0
                ? this.dataset.textEmpty
                : this.dataset.textAdd.replace('[count]', selected.length);
          }
        }

        if (this.selectAllEl) {
          const selectable = this.cards.filter(
            (card) => card.dataset.lookResolved === 'true'
          );

          this.selectAllEl.checked =
            selectable.length > 0 && selected.length === selectable.length;
        }
      }

      onSelectAll(event) {
        const checked = event.target.checked;

        this.cards.forEach((card) => {
          const checkbox = card.querySelector('[data-look-toggle]');

          if (checkbox && !checkbox.disabled) {
            checkbox.checked = checked;
          }

          this.syncCardState(card);
        });

        this.updateTotals();
      }

      async onSubmit(event) {
  event.preventDefault();

  if (!this.submitButton) return;
  if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

  const selected = this.selectedCards();
  if (selected.length === 0) return;

  this.setError();
  this.setLoading(true);

  const body = {
    items: selected.map((card) => ({
      id: card.lookVariant.id,
      quantity: 1,
    })),
  };

  try {
    /*
     * 1. Agregar los productos reales al carrito.
     * No solicitamos secciones en esta llamada porque este tema devuelve
     * el drawer incompleto cuando se agregan varios items simultáneamente.
     */
    const addResponse = await fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    });

    const addResult = await addResponse.json();

    if (!addResponse.ok || addResult.status) {
      const message =
        addResult.description ||
        addResult.message ||
        (window.cartStrings ? window.cartStrings.error : '');

      this.setError(message);
      return;
    }

    if (!this.cart) {
      window.location.href = window.routes.cart_url;
      return;
    }

    if (typeof this.cart.setActiveElement === 'function') {
      this.cart.setActiveElement(document.activeElement);
    }

    /*
     * 2. Solicitar las secciones actualizadas directamente desde /cart.
     * Se consultan por separado para evitar el bug del bundled response.
     */
    const cacheBuster = Date.now();

    const [drawerResponse, bubbleResponse] = await Promise.all([
      fetch(
        `${window.routes.cart_url}?section_id=cart-drawer&_=${cacheBuster}`,
        {
          method: 'GET',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
          cache: 'no-store',
        }
      ),
      fetch(
        `${window.routes.cart_url}?section_id=cart-icon-bubble&_=${cacheBuster}`,
        {
          method: 'GET',
          headers: {
            Accept: 'text/html',
            'X-Requested-With': 'XMLHttpRequest',
          },
          cache: 'no-store',
        }
      ),
    ]);

    if (!drawerResponse.ok || !bubbleResponse.ok) {
      throw new Error(
        `Cart refresh failed: drawer ${drawerResponse.status}, bubble ${bubbleResponse.status}`
      );
    }

    const [drawerHtml, bubbleHtml] = await Promise.all([
      drawerResponse.text(),
      bubbleResponse.text(),
    ]);

    /*
     * 3. Entregar al componente exactamente la estructura que espera.
     */
    this.cart.classList.remove('is-empty');

    this.cart.renderContents({
      id: selected[0].lookVariant.id,
      sections: {
        'cart-drawer': drawerHtml,
        'cart-icon-bubble': bubbleHtml,
      },
    });
  } catch (error) {
    console.error('[look-builder]', error);

    /*
     * El carrito pudo haberse actualizado aunque falle solo el drawer.
     * No mostramos un error falso; enviamos al carrito completo como fallback.
     */
    window.location.href = window.routes.cart_url;
  } finally {
    this.setLoading(false);
  }
}

      setLoading(loading) {
        if (!this.submitButton) return;

        const spinner = this.submitButton.querySelector('.loading__spinner');

        this.submitButton.classList.toggle('loading', loading);
        this.submitButton.setAttribute('aria-disabled', loading ? 'true' : 'false');

        if (spinner) {
          spinner.classList.toggle('hidden', !loading);
        }
      }

      setError(message) {
        if (!this.errorWrapper) return;

        this.errorWrapper.toggleAttribute('hidden', !message);

        if (message && this.errorMessage) {
          this.errorMessage.textContent = message;
        }
      }

      renderPrice(variant) {
        const onSale = variant.cap && variant.cap > variant.p;
        let html = '';

        if (onSale) {
          html += `<s class="price-item price-item--regular">${this.formatMoney(
            variant.cap
          )}</s> `;

          html += `<span class="price-item price-item--sale price-item--last">${this.formatMoney(
            variant.p
          )}</span>`;
        } else {
          html += `<span class="price-item price-item--regular">${this.formatMoney(
            variant.p
          )}</span>`;
        }

        return html;
      }

      formatMoney(cents) {
        const format = this.moneyFormat;
        const placeholder = /\{\{\s*(\w+)\s*\}\}/;

        const defaultTo = (value, fallback) =>
          value === null || value === undefined ? fallback : value;

        const formatWithDelimiters = (number, precision, thousands, decimal) => {
          precision = precision === null || precision === undefined ? 2 : precision;
          thousands = defaultTo(thousands, ',');
          decimal = defaultTo(decimal, '.');

          if (isNaN(number) || number === null) return 0;

          number = (number / 100.0).toFixed(precision);

          const parts = number.split('.');

          const dollars = parts[0].replace(
            /(\d)(?=(\d\d\d)+(?!\d))/g,
            `$1${thousands}`
          );

          const centsValue = parts[1] ? decimal + parts[1] : '';

          return dollars + centsValue;
        };

        const match = format.match(placeholder);

        if (!match) {
          return format;
        }

        let value = '';

        switch (match[1]) {
          case 'amount':
            value = formatWithDelimiters(cents, 2);
            break;
          case 'amount_no_decimals':
            value = formatWithDelimiters(cents, 0);
            break;
          case 'amount_with_comma_separator':
            value = formatWithDelimiters(cents, 2, '.', ',');
            break;
          case 'amount_with_period_separator':
            value = formatWithDelimiters(cents, 2, ' ', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = formatWithDelimiters(cents, 0, '.', ',');
            break;
          case 'amount_no_decimals_with_space_separator':
            value = formatWithDelimiters(cents, 0, ' ');
            break;
          case 'amount_with_apostrophe_separator':
            value = formatWithDelimiters(cents, 2, "'");
            break;
          default:
            value = formatWithDelimiters(cents, 2);
        }

        return format.replace(placeholder, value);
      }
    }
  );
}